/**
 * One notification connection per browser instead of one per tab.
 *
 * Operations opens every sale in its own tab by design, so a busy afternoon means a dozen
 * tabs of the admin portal, each holding its own SSE connection to
 * `/communicator/notifications/stream` and each replaying the initial flush when it opens.
 * Measured on 2026-09-19: 852 stream opens in the peak hour, on a Saturday.
 *
 * The standard fix, and the one here: elect a leader with the Web Locks API. Exactly one tab
 * holds the lock, and only that tab opens a connection; it relays what arrives to the others
 * over a BroadcastChannel. The lock is released by the browser when its tab closes or
 * crashes, so the next tab waiting on it takes over with no coordination of our own.
 *
 * Where either API is missing the helpers say so and the hook keeps today's behaviour, a
 * connection per tab.
 */

const STREAM_LOCK_NAME = 'fairdee:notifications:stream';
const STREAM_CHANNEL_NAME = 'fairdee:notifications';

export type StreamBroadcast =
  | { type: 'connected' }
  | { type: 'disconnected' }
  /** A `notification` frame, already parsed by the leader. */
  | { type: 'notification'; payload: unknown };

export function supportsCrossTabStream(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof BroadcastChannel !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    typeof navigator.locks?.request === 'function'
  );
}

export function openStreamChannel(): BroadcastChannel | null {
  if (!supportsCrossTabStream()) return null;
  try {
    return new BroadcastChannel(STREAM_CHANNEL_NAME);
  } catch {
    return null;
  }
}

/**
 * Take the stream lock and hold it for as long as this tab wants it.
 *
 * `onAcquired` fires once, when this tab becomes the leader. The returned function stands
 * down: it resolves the promise the lock is waiting on, which hands leadership to whichever
 * tab is next in the queue.
 *
 * Requests are queued by the browser, not rejected, so every follower is already lined up to
 * take over the moment the leader goes away.
 */
export function requestStreamLeadership(onAcquired: () => void): () => void {
  if (!supportsCrossTabStream()) {
    // No election possible: this tab leads itself, exactly as every tab does today.
    onAcquired();
    return () => {};
  }

  let standDown: (() => void) | undefined;
  let cancelled = false;

  const heldUntilStandDown = new Promise<void>((resolve) => {
    standDown = resolve;
  });

  void navigator.locks
    .request(STREAM_LOCK_NAME, () => {
      // Unmounted while queued: release immediately so the next tab is not kept waiting.
      if (cancelled) return Promise.resolve();
      onAcquired();
      return heldUntilStandDown;
    })
    .catch(() => {
      // A lock manager error should not cost this tab its notifications; lead alone.
      if (!cancelled) onAcquired();
    });

  return () => {
    cancelled = true;
    standDown?.();
  };
}
